import React, { useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { useBranch } from "@/context/BranchContext";
import { dateTimeFmt, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ScrollText, RotateCcw, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const ACTION_COLORS = {
  sale: "bg-blue-100 text-blue-700",
  purchase: "bg-emerald-100 text-emerald-700",
  price_change: "bg-amber-100 text-amber-700",
  adjustment: "bg-rose-100 text-rose-700",
  opname: "bg-violet-100 text-violet-700",
  open_shift: "bg-slate-100 text-slate-700",
  close_shift: "bg-slate-100 text-slate-700",
  special_sale: "bg-rose-100 text-rose-700",
};

const ACTION_OPTIONS = [
  { value: "sale", label: "Penjualan" },
  { value: "special_sale", label: "Penjualan Khusus" },
  { value: "purchase", label: "Pembelian" },
  { value: "price_change", label: "Ubah Harga" },
  { value: "adjustment", label: "Penyesuaian Stok" },
  { value: "opname", label: "Stock Opname" },
  { value: "stock_initial", label: "Stok Awal" },
  { value: "transfer_out", label: "Kirim Transfer" },
  { value: "transfer_in", label: "Terima Transfer" },
  { value: "return", label: "Retur" },
  { value: "open_shift", label: "Buka Shift" },
  { value: "close_shift", label: "Tutup Shift" },
  { value: "create", label: "Buat Data" },
  { value: "update", label: "Ubah Data" },
  { value: "purchase_order", label: "Order Pembelian (PO)" },
  { value: "accept", label: "Terima PO" },
  { value: "ready", label: "PO Siap Dijemput" },
  { value: "fulfill", label: "Penuhi PO" },
  { value: "food_order", label: "Pesanan Siap Saji" },
  { value: "food_prepare", label: "Siapkan Pesanan" },
  { value: "food_pickup", label: "Ambil Pesanan" },
  { value: "food_settle", label: "Setoran Pesanan" },
  { value: "menu_perm", label: "Hak Akses Menu" },
  { value: "reset_transactions", label: "Reset Transaksi" },
];

const ACTION_LABELS = ACTION_OPTIONS.reduce((m, a) => { m[a.value] = a.label; return m; }, {});

export default function AuditLog() {
  const { branches } = useBranch();
  const branchOptions = useMemo(
    () => branches.filter((b) => b.active !== false),
    [branches]
  );
  const branchNameMap = useMemo(() => {
    const m = {};
    branches.forEach((b) => { m[b.id] = b.name; });
    return m;
  }, [branches]);

  const [start, setStart] = useState(todayISO().slice(0, 8) + "01");
  const [end, setEnd] = useState(todayISO());
  const [branchId, setBranchId] = useState("all");
  const [action, setAction] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const load = () => {
    setLoading(true);
    setSearched(true);
    setPage(1);
    const params = { start, end };
    if (branchId !== "all") params.branch_id = branchId;
    if (action !== "all") params.action = action;
    client.get("/audit-logs", { params })
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilter = () => {
    setStart(todayISO().slice(0, 8) + "01");
    setEnd(todayISO());
    setBranchId("all");
    setAction("all");
    setRows([]);
    setSearched(false);
    setPage(1);
  };

  return (
    <div className="p-6 space-y-5 animate-in-up" data-testid="audit-log-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
          <ScrollText className="h-7 w-7 text-indigo-600" /> Audit Log
        </h1>
        <p className="text-sm text-muted-foreground">
          Catatan perubahan harga, penyesuaian stok, void & aksi penting — tersaring sesuai periode & cabang.
        </p>
      </div>

      {/* ---------- FILTER ---------- */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Dari</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
            className="w-40" data-testid="audit-filter-start" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Sampai</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
            className="w-40" data-testid="audit-filter-end" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Cabang</Label>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger className="w-48" data-testid="audit-filter-branch"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Cabang</SelectItem>
              {branchOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Aksi</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-48" data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Aksi</SelectItem>
              {ACTION_OPTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} data-testid="audit-filter-apply">Terapkan</Button>
        <Button variant="outline" onClick={resetFilter} data-testid="audit-filter-reset">
          <RotateCcw className="mr-1 h-4 w-4" /> Reset
        </Button>
      </div>

      <div className="rounded-lg border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Waktu</TableHead>
              <TableHead>Pengguna</TableHead>
              <TableHead>Cabang</TableHead>
              <TableHead>Aksi</TableHead>
              <TableHead>Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>}
            {!loading && searched && pagedRows.map((a) => (
              <TableRow key={a.id} className="hover:bg-slate-50" data-testid={`audit-row-${a.id}`}>
                <TableCell className="whitespace-nowrap text-xs">{dateTimeFmt(a.created_at)}</TableCell>
                <TableCell className="text-sm">{a.user_name || "sistem"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{branchNameMap[a.branch_id] || "—"}</TableCell>
                <TableCell><Badge className={ACTION_COLORS[a.action] || "bg-slate-100 text-slate-700"}>{ACTION_LABELS[a.action] || a.action}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground">{a.detail}</TableCell>
              </TableRow>
            ))}
            {!loading && !searched && <TableRow><TableCell colSpan={5} className="py-12 text-center text-muted-foreground" data-testid="audit-empty-initial">Atur filter lalu klik <b>Terapkan</b> untuk menampilkan data audit log</TableCell></TableRow>}
            {!loading && searched && !rows.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Tidak ada aktivitas pada filter ini</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {searched && rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="audit-pagination">
          <div className="text-sm text-muted-foreground" data-testid="audit-result-info">
            Menampilkan {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} dari {rows.length} aktivitas
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="audit-page-prev">
              <ChevronLeft className="h-4 w-4" /> Sebelumnya
            </Button>
            <span className="text-sm" data-testid="audit-page-indicator">Hal. {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="audit-page-next">
              Berikutnya <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
